import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo.js';
import { PillarIcon, type PillarIconName } from '../components/PillarIcon.js';
import { ScrollVideoHero } from '../components/ScrollVideoHero.js';
import { useReveal } from '../hooks/useReveal.js';
import { labels } from '../labels.js';
import styles from './VitrinePage.module.css';

const t = labels.vitrine;

const VIDEO_SRC = '/vitrine/scan-a-table.mp4';
const VIDEO_POSTER = '/vitrine/scan-a-table-poster.jpg';
const DISH_PHOTO = '/vitrine/plat-poulet-braise.jpg';
/** Where the subject sits in the hero video: the QR tent on the right, then the plate in the centre. */
const VIDEO_FOCUS = { from: 84, to: 42 };

/**
 * Restaurant back-office URL. Optional: without it, in production, the
 * restaurant link is simply not shown. See docs/12-deploiement.md.
 */
const ADMIN_BASE: string | null =
  import.meta.env.VITE_ADMIN_URL || (import.meta.env.DEV ? 'http://localhost:5174' : null);

/**
 * The links go straight to the sign-in screen, not to the back-office root:
 * the root sends a visitor who is not signed in back here.
 */
const ADMIN_URL: string | null = ADMIN_BASE ? `${ADMIN_BASE.replace(/\/+$/, '')}/connexion` : null;

/** Scroll distance, in px, after which the navigation bar turns opaque. */
function useScrolledPast(threshold: () => number): boolean {
  const [past, setPast] = useState(false);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      setPast(window.scrollY > threshold());
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [threshold]);
  return past;
}

/**
 * The moment the hero stage stops sticking and starts scrolling away: from
 * there its text slides under the bar, which must already be opaque.
 */
const heroEnd = () => {
  const hero = document.getElementById('vitrine-hero');
  return hero ? hero.offsetTop + hero.offsetHeight - window.innerHeight : window.innerHeight;
};

export function VitrinePage() {
  const solidNav = useScrolledPast(heroEnd);
  const pillarsRef = useReveal<HTMLElement>();
  const stepsRef = useReveal<HTMLElement>();
  const dashboardRef = useReveal<HTMLElement>();
  const ctaRef = useReveal<HTMLElement>();

  useEffect(() => {
    const previous = document.title;
    document.title = `${labels.app.name} - ${t.footer.signature}`;
    return () => { document.title = previous; };
  }, []);

  return (
    <div className={styles.page}>
      <a href="#fonctionnalites" className={styles.skip}>{t.nav.features}</a>

      <header className={`${styles.nav} ${solidNav ? styles.navSolid : ''}`}>
        <div className={styles.navInner}>
          <Link to="/" className={styles.brand} aria-label={labels.app.name}>
            <Logo size={40} withWordmark tone={solidNav ? 'light' : 'dark'} />
          </Link>
          <nav aria-label={t.nav.label} className={styles.navLinks}>
            <a href="#fonctionnalites">{t.nav.features}</a>
            <a href="#comment-ca-marche">{t.nav.howItWorks}</a>
            <a href="#dashboard">{t.nav.dashboard}</a>
          </nav>
          <div className={styles.navActions}>
            {ADMIN_URL && (
              <a href={ADMIN_URL} className={styles.navGhost}>{t.nav.restaurantSpace}</a>
            )}
            <Link to="/scan" className={`btn-primary ${styles.navCta}`}>{t.nav.scan}</Link>
          </div>
        </div>
      </header>

      <main>
        <div id="vitrine-hero">
          <ScrollVideoHero
            src={VIDEO_SRC}
            poster={VIDEO_POSTER}
            portraitFocus={VIDEO_FOCUS}
            videoLabel={t.hero.videoLabel}
            chapters={t.hero.chapters}
            scrollHint={t.hero.scrollHint}
            progressLabel={t.hero.progressLabel}
            actions={
              <>
                <Link to="/scan" className="btn-primary btn-lg">{t.cta.scan}</Link>
                {ADMIN_URL && <a href={ADMIN_URL} className="btn-secondary btn-lg">{t.cta.restaurant}</a>}
              </>
            }
          />
        </div>

        <section id="fonctionnalites" ref={pillarsRef} className={styles.section}>
          <div className={styles.container}>
            <header className={styles.sectionHead} data-reveal>
              <p className="eyebrow">{t.pillars.eyebrow}</p>
              <h2 className={styles.sectionTitle}>{t.pillars.title}</h2>
            </header>
            <ul className={styles.pillars}>
              {t.pillars.items.map((p) => (
                <li key={p.key} className={styles.pillar} data-reveal>
                  <span className={styles.pillarIcon}>
                    <PillarIcon name={p.key as PillarIconName} />
                  </span>
                  <h3 className={styles.pillarTitle}>
                    {p.title} <span>{p.subtitle}</span>
                  </h3>
                  <p className={styles.pillarText}>{p.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="comment-ca-marche" ref={stepsRef} className={`${styles.section} ${styles.sectionSand}`}>
          <div className={`${styles.container} ${styles.split}`}>
            <div>
              <header className={styles.sectionHead} data-reveal>
                <p className="eyebrow">{t.steps.eyebrow}</p>
                <h2 className={styles.sectionTitle}>{t.steps.title}</h2>
              </header>
              <ol className={styles.steps}>
                {t.steps.items.map((s, i) => (
                  <li key={s.title} className={styles.step} data-reveal>
                    <span className={styles.stepNumber} aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                    <div>
                      <h3 className={styles.stepTitle}>{s.title}</h3>
                      <p className={styles.stepText}>{s.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Client menu mock-up, built from the design system components. */}
            <div className={styles.phoneWrap} data-reveal aria-hidden="true">
              <div className={styles.phone}>
                <div className={styles.phoneScreen}>
                  <div className={styles.phoneHero}>
                    <img src={DISH_PHOTO} alt="" loading="lazy" />
                    <div className={styles.phoneHeroText}>
                      <strong>{t.phone.dishes[1].name}</strong>
                      <span>{t.phone.dishes[1].text}</span>
                    </div>
                  </div>
                  <p className={styles.phoneTitle}>{t.phone.title}</p>
                  <div className={styles.phoneChips}>
                    {t.phone.categories.map((c, i) => (
                      <span key={c} className={`chip ${i === 0 ? 'chip--active' : ''}`}>{c}</span>
                    ))}
                  </div>
                  <div className={styles.phoneList}>
                    {t.phone.dishes.map((d, i) => (
                      <div key={d.name} className="dish-row">
                        {i === 1
                          ? <img className="dish-thumb" src={DISH_PHOTO} alt="" loading="lazy" />
                          : <span className="dish-thumb" />}
                        <span className={styles.dishText}>
                          <strong>{d.name}</strong>
                          <span className="text-caption">{d.text}</span>
                        </span>
                        <span className="price">{d.price}</span>
                      </div>
                    ))}
                  </div>
                  <span className={`btn-primary btn-block ${styles.phoneCta}`}>{t.phone.cta}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="dashboard" ref={dashboardRef} className={styles.section}>
          <div className={`${styles.container} ${styles.split} ${styles.splitReverse}`}>
            <div>
              <header className={styles.sectionHead} data-reveal>
                <p className="eyebrow">{t.dashboard.eyebrow}</p>
                <h2 className={styles.sectionTitle}>{t.dashboard.title}</h2>
                <p className={styles.lead}>{t.dashboard.text}</p>
              </header>
              {ADMIN_URL && (
                <a href={ADMIN_URL} className="btn-outline" data-reveal>{t.cta.restaurant}</a>
              )}
            </div>

            <figure className={styles.board} data-reveal>
              <div className={styles.kpis}>
                {t.dashboard.kpis.map((k) => (
                  <div key={k.label} className="card">
                    <p className="kpi-label">{k.label}</p>
                    <p className="kpi-value">{k.value}</p>
                    <p className={`trend-up ${styles.trend}`}>{k.trend}</p>
                  </div>
                ))}
              </div>
              <div className={`card ${styles.recent}`}>
                <p className={styles.recentTitle}>{t.dashboard.recent}</p>
                {t.dashboard.orders.map((o) => (
                  <div key={o.number} className={styles.recentRow}>
                    <span className="tabular">{o.number}</span>
                    <span className="tabular">{o.amount}</span>
                    <span className={`badge badge--${o.status}`}>{o.label}</span>
                  </div>
                ))}
              </div>
              <figcaption className="visually-hidden">{t.dashboard.illustration}</figcaption>
            </figure>
          </div>
        </section>

        <section ref={ctaRef} className={styles.cta}>
          <div className={styles.container} data-reveal>
            <h2 className={styles.ctaTitle}>{t.cta.title}</h2>
            <p className={styles.ctaText}>{t.cta.text}</p>
            <div className={styles.ctaActions}>
              <Link to="/scan" className="btn-primary btn-lg">{t.cta.scan}</Link>
              {ADMIN_URL && <a href={ADMIN_URL} className="btn-secondary btn-lg">{t.cta.restaurant}</a>}
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={`${styles.container} ${styles.footerInner}`}>
          <Logo size={28} withWordmark />
          <p>{t.footer.signature}</p>
        </div>
      </footer>
    </div>
  );
}
